import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';

const AdminLogin = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const existingAdminToken = localStorage.getItem("admin_token");
    const existingAdminUser = JSON.parse(localStorage.getItem("admin_user") || "{}");
    if (existingAdminToken && existingAdminUser.role === "super_admin") {
      navigate("/admin");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier || !password) {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle rate limiting specifically
        if (response.status === 429) {
          setError(data.message || 'Too many failed login attempts. This IP has been locked for 10 minutes.');
        } else {
          setError(data.message || 'Invalid credentials.');
        }
        setLoading(false);
        return;
      }

      const token = data.token;

      // CRITICAL UI GUARD: Check for super_admin role
      if (data.user?.role !== 'super_admin') {
        setError('Access Denied: You do not have administrator privileges.');
        setLoading(false);
        return;
      }

      // Isolated admin token and user storage
      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));

      // Redirect to admin overview page
      navigate('/admin');
    } catch (err) {
      console.error(err);
      setError('A network error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-[#0B0F19] p-4">
      <div className="w-full max-w-md bg-[#0F172A] border border-[#1E293B] rounded-2xl shadow-2xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-[#6CD800] text-4xl font-black tracking-wider font-bebas">FITSCAN</h1>
          <p className="text-gray-400 text-sm font-semibold tracking-wide uppercase mt-1">Super Admin Portal</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Phone Number or Email
            </label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full px-4 py-3 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6CD800] focus:ring-1 focus:ring-[#6CD800] transition-colors"
              placeholder="e.g. 9876543210 or admin@fitscan.in"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6CD800] focus:ring-1 focus:ring-[#6CD800] transition-colors"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 bg-[#6CD800] hover:bg-[#5bbf00] text-[#0B0F19] font-bold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#6CD800]/50 ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Authenticating...' : 'LOG IN'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
